import {
  TRANSFORM_EXPLOIT_FLAG,
  categoryToTitle,
} from "@sscs/report/constants";
import { capitalizeFirstLetter } from "@sscs/utils";
import { useGetReportMappingsQuery } from "Services";
import useSearch from "../../../hooks/useSearch";
import { Insight, InsightsFilters } from "../../../types";

type SearchAction = {
  label: string;
  presentInFilter?: boolean;
  isNegativeFilter?: boolean;
  tooltip?: string;
  onClick: () => void;
};

const getCommonActionProps = (
  filters: InsightsFilters,
  actionFilter: keyof InsightsFilters,
  value: string,
): Partial<SearchAction> => {
  return {
    presentInFilter:
      filters[actionFilter] !== undefined &&
      filters[actionFilter]?.values.includes(value),
    isNegativeFilter:
      (filters[actionFilter] !== undefined &&
        filters[actionFilter]?.operation === "is_not") ||
      filters[actionFilter]?.operation === "is_not_any",
  };
};

export const useSearchActions = (issue: Insight): SearchAction[] => {
  const { data: reportMappings } = useGetReportMappingsQuery();
  const { filters, handleChipClick } = useSearch();
  const actions: SearchAction[] = [];

  actions.push({
    ...getCommonActionProps(filters, "policy_category", issue.policy_category),
    label: categoryToTitle[issue.policy_category],
    onClick: () =>
      handleChipClick("policy_category", {
        operation: "is_any",
        values: [issue.policy_category],
      }),
  });

  if (issue.issue_type === "vulnerability") {
    issue.exploit.forEach((tag) =>
      actions.push({
        ...getCommonActionProps(filters, "exploit", tag),
        label: TRANSFORM_EXPLOIT_FLAG[tag],
        onClick: () =>
          handleChipClick("exploit", {
            operation: "all_of",
            values: [tag],
          }),
      }),
    );
  } else if (issue.issue_type === "malware") {
    const [threatPlatform, threatType, threatFamily] = issue.name.split(".");

    actions.push({
      ...getCommonActionProps(filters, "classification", issue.classification),
      label: issue.classification,
      onClick: () =>
        handleChipClick("classification", {
          operation: "is_any",
          values: [issue.classification],
        }),
    });

    actions.push({
      ...getCommonActionProps(filters, "threat_platform", threatPlatform),
      label: threatPlatform,
      onClick: () =>
        handleChipClick("threat_platform", {
          operation: "is_any",
          values: [threatPlatform],
        }),
    });
    actions.push({
      ...getCommonActionProps(filters, "threat_type", threatType),
      label: threatType,
      onClick: () =>
        handleChipClick("threat_type", {
          operation: "is_any",
          values: [threatType],
        }),
    });
    actions.push({
      ...getCommonActionProps(filters, "threat_name", threatFamily),
      label: threatFamily,
      onClick: () =>
        handleChipClick("threat_name", {
          operation: "is_any",
          values: [threatFamily],
        }),
    });
  } else if (issue.issue_type === "license") {
    actions.push({
      ...getCommonActionProps(filters, "license_family", issue.license_family),
      label: issue.license_family,
      onClick: () =>
        handleChipClick("license_family", {
          operation: "is_any",
          values: [issue.license_family],
        }),
    });
  } else if (issue.issue_type === "secret") {
    actions.push({
      ...getCommonActionProps(filters, "liveness", issue.liveness),
      label: capitalizeFirstLetter(issue.liveness),
      onClick: () =>
        handleChipClick("liveness", {
          operation: "is_any",
          values: [issue.liveness],
        }),
    });
  }

  issue.caused_violations.forEach((policyId) =>
    actions.push({
      ...getCommonActionProps(filters, "policy_id", policyId),
      label: policyId,
      tooltip: reportMappings?.descriptions[policyId],
      onClick: () =>
        handleChipClick("policy_id", {
          operation: "is_any",
          values: [policyId],
        }),
    }),
  );

  return actions;
};
